import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { setMeta, META } from "@/lib/db";
import { ScanCTA } from "@/components/ScanCTA";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { ArrowRight, ScanLine } from "lucide-react";

const STEPS = ["welcome", "first_farm", "pair_reader", "first_animal"];

export default function Onboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [farm, setFarm] = useState({ name: "", location: "" });
  const [farmId, setFarmId] = useState(null);
  const [readerMode, setReaderMode] = useState("simulator");
  const [animal, setAnimal] = useState({ chip_code: "", lomo_number: "", sex: "female", breed: "Mestizo" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { /* RequireAuth ya nos protege; no necesitamos verificar user acá */ }, [user, navigate]);

  const progress = ((step + 1) / STEPS.length) * 100;

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));

  const saveFarm = async () => {
    if (!farm.name.trim()) return toast.error("Ponéle un nombre a tu finca");
    setSaving(true);
    try {
      const f = await api.createFarm(farm);
      setFarmId(f.id);
      toast.success("Finca creada");
      next();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pude crear la finca");
    } finally { setSaving(false); }
  };

  const saveReaderMode = async () => {
    await setMeta(META.READER_MODE, readerMode);
    toast.success("Lector configurado");
    next();
  };

  const saveAnimal = async () => {
    if (!animal.chip_code || animal.chip_code.length < 10) return toast.error("Ingresá un chip válido (mínimo 10 dígitos)");
    setSaving(true);
    try {
      await api.createAnimal({ ...animal, farm_id: farmId });
      toast.success("¡Primer animal cargado!");
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pude cargar el animal");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] relative">
      <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_20%_0%,rgba(16,185,129,0.10),transparent_55%)] noise pointer-events-none" aria-hidden />
      <div className="relative max-w-xl mx-auto px-4 py-10">
        <div className="mb-6">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
            <span>Paso {step + 1} de {STEPS.length}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-1.5" />
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 sm:p-8">
          {step === 0 && (
            <div className="space-y-5">
              <Logo variant="mark" className="h-14 w-14" />
              <h2 className="font-heading text-2xl sm:text-3xl font-semibold">
                Bienvenido a <span>Cuenta</span><span className="text-[hsl(var(--primary))]"> Vaca</span>
              </h2>
              <p className="text-muted-foreground">Inventario rápido, sin internet, con lector RFID. Vamos a configurarlo en 3 pasos cortos.</p>
              <Button data-testid="onboarding-welcome-continue" onClick={next} className="h-12 bg-emerald-700 hover:bg-emerald-800 text-base px-6">
                Arrancar <ArrowRight size={18} className="ml-1" />
              </Button>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <h2 className="font-heading text-2xl font-semibold">Creá tu primera finca</h2>
              <p className="text-sm text-muted-foreground">Podés sumar más fincas y lotes después, desde Ajustes.</p>
              <div className="space-y-3">
                <div>
                  <Label>Nombre de la finca</Label>
                  <Input data-testid="onboarding-farm-name" value={farm.name} onChange={(e)=>setFarm({ ...farm, name: e.target.value })} className="h-11" placeholder="La Esperanza" />
                </div>
                <div>
                  <Label>Ubicación (opcional)</Label>
                  <Input data-testid="onboarding-farm-location" value={farm.location} onChange={(e)=>setFarm({ ...farm, location: e.target.value })} className="h-11" placeholder="Provincia, país" />
                </div>
              </div>
              <Button data-testid="onboarding-first-farm-submit" onClick={saveFarm} disabled={saving} className="h-12 bg-emerald-700 hover:bg-emerald-800 w-full">{saving ? "Guardando…" : "Guardar finca"}</Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <h2 className="font-heading text-2xl font-semibold">Emparejá el lector</h2>
              <p className="text-sm text-muted-foreground">Elegí el modo según tu dispositivo. Usá el <b>lector físico (HID)</b> o el <b>Simulador</b> para probar.</p>
              <div>
                <Label>Modo del lector</Label>
                <Select value={readerMode} onValueChange={setReaderMode}>
                  <SelectTrigger data-testid="onboarding-reader-mode-select" className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="simulator">Simulador (probar sin lector)</SelectItem>
                    <SelectItem value="hid">Lector RFID (lector físico)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 flex-col sm:flex-row">
                <Button data-testid="onboarding-reader-connect-button" onClick={saveReaderMode} className="h-12 bg-emerald-700 hover:bg-emerald-800 flex-1">
                  <ScanLine size={18} className="mr-1" /> Continuar
                </Button>
                <Button data-testid="onboarding-reader-skip" variant="outline" onClick={next} className="h-12">Lo hago después</Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <h2 className="font-heading text-2xl font-semibold">Cargá tu primer animal</h2>
              <p className="text-sm text-muted-foreground">Podés escanear el chip o tipearlo a mano. Más datos los completamos después.</p>
              <div className="space-y-3">
                <div>
                  <Label>Código de chip RFID</Label>
                  <div className="flex gap-2">
                    <Input data-testid="onboarding-animal-chip" value={animal.chip_code} onChange={(e)=>setAnimal({ ...animal, chip_code: e.target.value })} className="h-11 flex-1 font-mono" placeholder="900164000..." />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">15 dígitos. Si escaneás, se completa solo.</p>
                </div>
                <ScanCTA testId="onboarding-first-animal-scan-button" onChip={(chip)=>setAnimal((a)=>({ ...a, chip_code: chip }))} label="Escanear chip" />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Lomo</Label>
                    <Input data-testid="onboarding-animal-lomo" value={animal.lomo_number} onChange={(e)=>setAnimal({ ...animal, lomo_number: e.target.value })} className="h-11" placeholder="A-1" />
                  </div>
                  <div>
                    <Label>Raza</Label>
                    <Input data-testid="onboarding-animal-breed" value={animal.breed} onChange={(e)=>setAnimal({ ...animal, breed: e.target.value })} className="h-11" />
                  </div>
                </div>
                <div>
                  <Label>Sexo</Label>
                  <Select value={animal.sex} onValueChange={(v)=>setAnimal({ ...animal, sex: v })}>
                    <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="female">Hembra</SelectItem>
                      <SelectItem value="male">Macho</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-2">
                <Button data-testid="onboarding-first-animal-submit" onClick={saveAnimal} disabled={saving} className="h-12 bg-emerald-700 hover:bg-emerald-800 flex-1">
                  {saving ? "Guardando…" : "Cargar animal"}
                </Button>
                <Button data-testid="onboarding-skip-animal" variant="outline" className="h-12" onClick={()=>navigate("/")}>Saltar</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
